import { Link, useLocation } from "react-router-dom";
import { Compass } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";

/**
 * 404 page.
 *
 * Replaces a blanket `<Navigate to="/" replace />`, which sent every unknown
 * URL to the marketing landing. That was actively confusing for signed-in
 * users: a mistyped path or a link to a deleted job silently became a sales
 * pitch, with no error and no way back into the app.
 *
 * The destinations are chosen by auth state, because "home" means the landing
 * page to a visitor and the dashboard to a user.
 */
export default function NotFound() {
  const { user } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen grid place-items-center bg-paper px-6" data-testid="not-found">
      <div className="max-w-md text-center">
        <div className="inline-flex w-12 h-12 items-center justify-center rounded-sm border border-sand bg-paper mb-5">
          <Compass className="w-5 h-5 text-stone-600" />
        </div>
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600 mb-2">Error 404</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-stone-950">
          We can’t find that page.
        </h1>
        <p className="mt-3 text-sm text-stone-600">
          Nothing exists at{" "}
          <code className="px-1.5 py-0.5 bg-paper-deep rounded-sm text-stone-800 break-all">
            {location.pathname}
          </code>
          . It may have been moved, or deleted.
        </p>
        <div className="mt-7 flex flex-wrap gap-2 justify-center">
          <Button asChild className="rounded-sm bg-brand hover:bg-brand-hover text-white">
            <Link to={user ? "/dashboard" : "/"} data-testid="notfound-primary">
              {user ? "Back to dashboard" : "Back to home"}
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-sm border-sand">
            <Link to={user ? "/jobs" : "/pricing"} data-testid="notfound-secondary">
              {user ? "View my jobs" : "See pricing"}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
